/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useServiceContext } from '../../../root'
import Page from './Page'
import LoadingSpinner from '../../../components/LoadingSpinner'

interface PageWrapperProps {
  fileId: number
  title: string
  params?: any
  onClose?: () => void
}

const PageWrapper: React.FC<PageWrapperProps> = ({ fileId, title, params, onClose }) => {
  console.log('PageWrapper params', params)
  const context = useServiceContext()
  const { api } = context
  const { translate } = context.service.filter
  const [initValues, setInitValues] = useState<any>({ comment: '', expirationDate: null })
  const [isLoading, setIsLoading] = useState(true)
  const isFileToEtmfOrReclassifyAndSourceFileOwner =
    (params?.reclassify || params?.fileToEtmf) && params?.file && params?.file?.ownerId

  useEffect(() => {
    let isMounted = true
    setIsLoading(true)
    async function getFileAllDetails() {
      try {
        // 1. Get file
        const fileResp = await api.files.getFile(fileId)
        const file: any = fileResp
        const newInitValues: any = {
          comment: '',
          expirationDate: null,
          fileToShow: file
        }

        // 2. Set description based on file type
        switch (file?.type) {
          case 'REFERENCE':
            newInitValues.description = translate('CR_DESC_DEFAULT_PLACEHOLDER_EDIT')
            break
          case 'PLACEHOLDER':
            newInitValues.description = translate('CR_DESC_DEFAULT_PLACEHOLDER_EDIT_PLACEHOLDER')
            break
          case 'DOCUMENT':
            newInitValues.description = translate('CR_DESC_DEFAULT_PLACEHOLDER_EDIT')
            break
        }
        if (params?.reclassify) {
          newInitValues.description = translate('CR_DESC_DEFAULT_PLACEHOLDER_RECLASSIFY_AS_PLACEHOLDER')
        } else if (params?.fileToEtmf) {
          newInitValues.description = translate('CR_DESC_DEFAULT_PLACEHOLDER_FILE_ETMF_TO_PLACEHOLDER')
        }

        // 3. Get file details
        const fileDetailsResp = await api.files.getDetails(fileId)
        const fileDetails: any = fileDetailsResp
        // Transform date from server
        const transformDateFromServer = (metadata: any) => {
          const momentDate = moment.utc(metadata).local()
          return momentDate
        }
        if (fileDetails?.expiresAt) {
          const expDate = transformDateFromServer(fileDetails.expiresAt)
          newInitValues.expirationDate = expDate.isValid() ? expDate : null
        }

        // 4. Get docType
        if (fileDetails?.fsiTypeTemplate?.id) {
          const docTypeResp = await api.documentTypes.getById(fileDetails.fsiTypeTemplate.id)
          const docType: any = docTypeResp
          newInitValues.docType = docType
          if (docType.requireJustification) {
            newInitValues.justificationTextId = null
          }
          if (file?.type === 'PLACEHOLDER' && docType.autonamingEnabled && fileDetails.title) {
            newInitValues.fileName = fileDetails.title
          }
          if (!docType.requireExpirationDate && fileDetails.expired) {
            newInitValues.expirationDate = null
          }
        }

        // 5. Get CR Approvers
        const crApprovers = await api.files.getFileCRApprovers(fileId, true)
        newInitValues.crApprovers = crApprovers

        // 6. Get Editors
        const editors = await api.files.getFsiEditors(fileId, !isFileToEtmfOrReclassifyAndSourceFileOwner)
        newInitValues.editors = editors

        // 7. Get Reviewers
        const reviewers = await api.files.getFileReviewers(fileId)
        newInitValues.reviewers = reviewers

        // 8. Get Approvers
        const approvers = await api.files.getFileApprovers(fileId)
        newInitValues.approvers = approvers

        // 9. Periodic Review
        if (params?.isPeriodicReview) {
          newInitValues.prRemediationChangeReq = params?.isPeriodicReview
        }

        if (isMounted) setInitValues(newInitValues)
      } catch (e) {
        // Optionally handle error
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }
    getFileAllDetails()
    return () => {
      isMounted = false
    }
  }, [fileId, params, api.files, isFileToEtmfOrReclassifyAndSourceFileOwner])

  if (isLoading) {
    return (
      <div className="edit-document-loading-overlay">
        <LoadingSpinner size={40} />
      </div>
    )
  }
  return initValues.docType ? (
    <Page fileId={fileId} title={title} initValues={initValues} onClose={onClose} params={params} />
  ) : null
}

export default PageWrapper
